import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Icon } from "../../components/icons";
import { Alert, Button, Card, CardHeader, Mono, Select, StatusBadge, Switch, cx, type StatusTone } from "../../components/ui";
import { useApps, usePointDomain, useUpdateDomain } from "../../lib/queries";
import type { DomainPointingHost, RegisteredDomainDetail } from "../../lib/types";
import { LifeStrip, Nameservers, Orders, Records, Settings, TransferOut, useDomainTab } from "./DomainDetailPage";

/**
 * The four tabs of a domain, each reading the page's context. Overview is
 * the one somebody comes back for; the other three are where a thing is
 * changed once.
 */

export function DomainOverviewTab() {
  const { orgId, domain, canManage, canDeploy } = useDomainTab();
  return (
    <div className="space-y-6">
      <LifeStrip domain={domain} action={canManage ? <AutorenewSwitch orgId={orgId} domain={domain} /> : undefined} />
      {domain.managedDns ? (
        <WherePoints orgId={orgId} domain={domain} canDeploy={canDeploy} />
      ) : (
        <ElsewhereCard domain={domain} />
      )}
    </div>
  );
}

export function DomainDnsTab() {
  const { orgId, domain, canManage, canDeploy } = useDomainTab();
  return (
    <div className="space-y-6">
      {domain.managedDns ? <Records orgId={orgId} domain={domain} canEdit={canDeploy} /> : <ElsewhereCard domain={domain} />}
      {canManage && <Nameservers orgId={orgId} domain={domain} />}
    </div>
  );
}

export function DomainSettingsTab() {
  const { orgId, domain, currency, canManage } = useDomainTab();
  const { t } = useTranslation("domains");
  if (!canManage) return <Alert tone="info">{t("detail.adminOnly")}</Alert>;
  return (
    <div className="space-y-6">
      <Settings orgId={orgId} domain={domain} currency={currency} />
      <TransferOut orgId={orgId} domain={domain} />
    </div>
  );
}

export function DomainOrdersTab() {
  const { domain, currency } = useDomainTab();
  return <Orders domain={domain} currency={currency} />;
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

/** The renewal decision, on the strip that shows what it decides. */
function AutorenewSwitch({ orgId, domain }: { orgId: string; domain: RegisteredDomainDetail }) {
  const { t } = useTranslation("domains");
  const update = useUpdateDomain(orgId, domain.id);
  return (
    <label className="flex items-center gap-3 text-[13px] text-fg-muted">
      {t("detail.settings.autorenew")}
      <Switch
        checked={domain.autorenew}
        disabled={update.isPending}
        onChange={(next) => update.mutate({ autorenew: next })}
        label={t("detail.settings.autorenew")}
      />
    </label>
  );
}

type HostState = "pending" | "active" | "failed";
const HOST_TONE: Record<HostState, StatusTone> = { active: "good", pending: "warn", failed: "bad" };
/** The three states the sweep writes; anything else reads as pending. */
const hostState = (status: string): HostState => (status === "active" || status === "failed" ? status : "pending");

/**
 * Where the name goes, and whether it is working.
 *
 * Read from the app side rather than the zone: a record can be right while
 * the certificate is still being issued, and "is my domain working" is a
 * question about the certificate. Each host is one line with the same badge
 * the app's Domains tab wears, so the two never disagree.
 */
function WherePoints({ orgId, domain, canDeploy }: { orgId: string; domain: RegisteredDomainDetail; canDeploy: boolean }) {
  const { t } = useTranslation("domains");
  const { app, hosts } = domain.pointing;
  const [changing, setChanging] = useState(false);

  if (!app || changing) {
    return (
      <PointForm
        orgId={orgId}
        domain={domain}
        canDeploy={canDeploy}
        onCancel={app ? () => setChanging(false) : undefined}
        onDone={() => setChanging(false)}
      />
    );
  }

  return (
    <Card>
      <CardHeader
        title={t("detail.point.pointsAt", { app: app.name })}
        description={t("detail.point.pointsAtHint")}
        action={
          canDeploy ? (
            <Button variant="outline" size="sm" onClick={() => setChanging(true)}>
              {t("detail.point.change")}
            </Button>
          ) : undefined
        }
      />
      <ul className="divide-y divide-line">
        {hosts.map((host) => (
          <HostRow key={host.host} host={host} />
        ))}
      </ul>
    </Card>
  );
}

function HostRow({ host }: { host: DomainPointingHost }) {
  const { t } = useTranslation("domains");
  const state = hostState(host.status);
  const settled = state === "active";
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-3">
      <span aria-hidden className={cx("size-2 shrink-0 rounded-full", settled ? "bg-mint" : state === "failed" ? "bg-danger" : "bg-warn")} />
      <div className="min-w-0 flex-1">
        <Mono className="text-[14px] text-fg">{host.host}</Mono>
        <p className="text-[12px] text-fg-subtle">
          {state === "failed" && host.failureReason ? host.failureReason : t(`detail.point.host.${state}`)}
        </p>
      </div>
      <StatusBadge tone={HOST_TONE[state]} label={t(`detail.point.hostLabel.${state}`)} />
      {settled && (
        <a href={`https://${host.host}`} target="_blank" rel="noreferrer" className="text-fg-subtle hover:text-fg" aria-label={host.host}>
          <Icon name="arrow-up-right" className="size-3.5" />
        </a>
      )}
      <Link to={`/apps/${host.appId}/domains`} className="text-[12px] text-fg-subtle hover:text-fg">
        {t("detail.point.onApp")}
      </Link>
    </li>
  );
}

function PointForm({
  orgId,
  domain,
  canDeploy,
  onCancel,
  onDone,
}: {
  orgId: string;
  domain: RegisteredDomainDetail;
  canDeploy: boolean;
  onCancel?: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation("domains");
  const apps = useApps(orgId, 100);
  const [appId, setAppId] = useState("");
  const point = usePointDomain(orgId, domain.id);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (appId) point.mutate(appId, { onSuccess: onDone });
  };

  return (
    <Card>
      <CardHeader title={t("detail.sections.point")} description={t("detail.point.description")} />
      {!canDeploy ? (
        <p className="px-5 py-4 text-sm text-fg-muted">{t("detail.point.developerOnly")}</p>
      ) : (
        <form onSubmit={submit} className="flex flex-wrap items-center gap-3 px-5 py-4">
          <div className="min-w-56 flex-1">
            <Select value={appId} onChange={(e) => setAppId(e.target.value)} aria-label={t("detail.point.app")}>
              <option value="">{t("detail.point.choose")}</option>
              {(apps.data?.data ?? []).map((app) => (
                <option key={app.id} value={app.id}>
                  {app.name}
                </option>
              ))}
            </Select>
          </div>
          <Button type="submit" disabled={!appId} loading={point.isPending}>
            {t("detail.point.button")}
          </Button>
          {onCancel && (
            <Button type="button" variant="ghost" onClick={onCancel}>
              {t("detail.records.cancel")}
            </Button>
          )}
          {point.error && (
            <div className="basis-full">
              <Alert>{point.error.message}</Alert>
            </div>
          )}
        </form>
      )}
    </Card>
  );
}

/** The nameservers are elsewhere: the zone is not ours to show. */
function ElsewhereCard({ domain }: { domain: RegisteredDomainDetail }) {
  const { t } = useTranslation("domains");
  return (
    <Card>
      <CardHeader title={t("detail.sections.point")} />
      <div className="px-5 py-4 text-sm leading-6 text-fg-muted">
        <p>{t("detail.point.unmanaged")}</p>
        {domain.nameServers.length > 0 && (
          <p className="mt-2 font-mono text-[12px] text-fg-subtle">{domain.nameServers.join(" · ")}</p>
        )}
      </div>
    </Card>
  );
}
